import { PersonaType } from '@constants/global.enum';
import { SummaryItem } from '@interfaces/vendor.model';

export interface MarkedDate {
  date: string;
  type: string;
  label: string;
}

export class TimesheetCalendarHelper {

  processLeaveDates(data: any): MarkedDate[] {
    const markedDates: MarkedDate[] = [];

    if (Array.isArray(data.paidLeaveDates)) {
      data.paidLeaveDates.forEach((date: string) => {
        markedDates.push({ date, type: 'paid', label: 'Paid Leave' });
      });
    }
    if (Array.isArray(data.unpaidLeaveDates)) {
      data.unpaidLeaveDates.forEach((date: string) => {
        markedDates.push({ date, type: 'unpaid', label: 'Unpaid Leave' });
      });
    }
    if (Array.isArray(data.halfPaidLeaveDates)) {
      const halfPaidCounts = new Map<string, number>();
      data.halfPaidLeaveDates.forEach((date: string) => {
        const count = (halfPaidCounts.get(date) || 0) + 1;
        if (count <= 2) {
          halfPaidCounts.set(date, count);
          markedDates.push({ date, type: 'halfpaid', label: 'Half Paid Leave' });
        }
      });
    }
    if (Array.isArray(data.halfUnpaidLeaveDates)) {
      const halfUnpaidCounts = new Map<string, number>();
      data.halfUnpaidLeaveDates.forEach((date: string) => {
        const count = (halfUnpaidCounts.get(date) || 0) + 1;
        if (count <= 2) {
          halfUnpaidCounts.set(date, count);
          markedDates.push({ date, type: 'halfunpaid', label: 'Half Unpaid Leave' });
        }
      });
    }
    if (Array.isArray(data.publicHolidayDates)) {
      data.publicHolidayDates.forEach((date: string) => {
        markedDates.push({ date, type: 'public', label: 'Public Holiday' });
      });
    }
    if (Array.isArray(data.rtoDatesList)) {
      data.rtoDatesList.forEach((date: string) => {
        markedDates.push({ date, type: 'office', label: 'Office Day' });
      });
    }
    if (Array.isArray(data.remoteDatesList)) {
      data.remoteDatesList.forEach((date: string) => {
        markedDates.push({ date, type: 'remote', label: 'Remote Day' });
      });
    }
    if (Array.isArray(data.holidayDates)) {
      data.holidayDates.forEach((date: string) => {
        markedDates.push({ date, type: 'holiday', label: 'Holiday Date' });
      });;
    }
    return markedDates;
  }

  processLeaveSummary(
    data: any,
    SUMMARY_KEYS: any,
    SUMMARY_KEYS_ATTENDANCE: any,
    componentName: string,
  ): SummaryItem[] {
    if (
      componentName === PersonaType.Timesheet ||
      componentName === PersonaType.vendorRepTimesheet ||
      componentName === PersonaType.ContractorTimesheet ||
      componentName === PersonaType.BillingHours
    ) {
      if (data.quarterlyLimit === 0) {
        return [
          { text: SUMMARY_KEYS.UNPAID_LEAVES, value: data.unpaidLeaves || 0 },
          {
            text: SUMMARY_KEYS.QUARTERLY_AVAILED,
            value: data.quarterlyLeavesAvailed || 0,
          },
          { text: SUMMARY_KEYS.QUARTERLY_LIMIT, value: data.quarterlyLimit },
        ];
      } else {
        return [
          { text: SUMMARY_KEYS.TOTAL_LEAVES, value: data.totalLeaves || 0 },
          { text: SUMMARY_KEYS.PAID_LEAVES, value: data.paidLeaves || 0 },
          { text: SUMMARY_KEYS.UNPAID_LEAVES, value: data.unpaidLeaves || 0 },
          {
            text: SUMMARY_KEYS.QUARTERLY_AVAILED,
            value: data.quarterlyLeavesAvailed || 0,
          },
          { text: SUMMARY_KEYS.QUARTERLY_LIMIT, value: data.quarterlyLimit },
        ];
      }
    } else if (componentName === PersonaType.RtoContractorList) {
  const officeDaysCount = Array.isArray(data.rtoDatesList) ? data.rtoDatesList?.length : 0;
  const remoteDaysCount = Array.isArray(data.remoteDatesList) ? data.remoteDatesList?.length : 0;
  const absentDaysCount = Array.isArray(data.paidLeaveDates) ? data.paidLeaveDates?.length : 0;
  const holidayDaysCount = Array.isArray(data.holidayDates) ? data.holidayDates?.length : 0;
  return [
    { text: SUMMARY_KEYS_ATTENDANCE.OFFICE_DAYS, value: officeDaysCount },
    { text: SUMMARY_KEYS_ATTENDANCE.REMOTE_DAYS, value: remoteDaysCount },
    { text: SUMMARY_KEYS_ATTENDANCE.ABSENT_DAYS, value: absentDaysCount },
    { text: SUMMARY_KEYS_ATTENDANCE.HOLIDAY_DAYS, value: holidayDaysCount },
  ];
}
    return [];
  }

  getLeaveTypes(
    summary: SummaryItem[],
    SUMMARY_KEYS: any,
  ): { value: string; label: string }[] {
    const quarterlyLimit =
      summary.find((item) => item.text === SUMMARY_KEYS.QUARTERLY_LIMIT)
        ?.value || 0;
    if (quarterlyLimit === 0) {
      return [{ value: 'halfunpaid', label: 'Half Unpaid Leave' },
        { value: 'unpaid', label: 'Unpaid Leave' }];
    }
    return [
      { value: 'halfpaid', label: 'Half Paid Leave' },
      { value: 'paid', label: 'Paid Leave' },
      { value: 'halfunpaid', label: 'Half Unpaid Leave' },
      { value: 'unpaid', label: 'Unpaid Leave' },
    ];
  }
  dateToString(date: Date): string {
    return (
      date.getFullYear() +
      '-' +
      String(date.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(date.getDate()).padStart(2, '0')
    );
  }
  formatDate(dateString: string | number | Date): string {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  displayMonth(
    dates: { date: string; label: string }[],
    today: { date: string; text: string }[],
    errorHandler: (error: any, message: string) => void,
  ): void {
    setTimeout(() => {
      const cells = document.querySelectorAll('.mat-calendar-body-cell');

      cells.forEach((cell) => {
        const htmlCell = cell as HTMLElement;
        const ariaLabel = htmlCell.getAttribute('aria-label');
        if (!ariaLabel) return;

        try {
          const dateSearch = this.dateToString(new Date(ariaLabel));
          const data = dates.find((f) => f.date === dateSearch);
          const data_today = today.find((f) => f.date === dateSearch);
          if (data) htmlCell.setAttribute('aria-label', data.label);
          if (data_today) htmlCell.setAttribute('aria-label', data_today.text);
        } catch (error) {
          errorHandler(error, 'Failed to fetch leave summary');
        }
      });
    });
  }
  streamOpened(renderer: any, displayMonthCallback: () => void): void {
    setTimeout(() => {
      const buttons = document.querySelectorAll(
        'mat-calendar .mat-icon-button',
      );
      buttons.forEach((btn) =>
        renderer.listen(btn, 'click', () => {
          setTimeout(() => {
            displayMonthCallback();
          });
        }),
      );
      displayMonthCallback();
    });
  }
  updateCalendarCells(
    selectedDate: Date | null,
    apiMarkedDates: { date: string; type: string }[],
    type: string,
  ): void {
    setTimeout(() => {
      const cells = document.querySelectorAll('.mat-calendar-body-cell');
      cells.forEach((cell) => {
        const htmlCell = cell as HTMLElement;
        const aria = htmlCell.getAttribute('aria-label');
        if (!aria) return;

        const cellDate = new Date(aria);
        if (
          selectedDate &&
          cellDate.toDateString() === selectedDate.toDateString()
        ) {
          htmlCell
            .querySelector('.mat-calendar-body-cell-content')
            ?.classList.remove('paid', 'unpaid', 'halfpaid', 'halfunpaid');
          htmlCell
            .querySelector('.mat-calendar-body-cell-content')
            ?.classList.add(
              type === 'paid' ? 'paid' : 
              type === 'unpaid' ? 'unpaid' : 
              type === 'halfpaid' ? 'halfpaid' : 
              type === 'halfunpaid' ? 'halfunpaid' : 
              'unpaid'
            );
        }
      });
    });
  }
}